import React, { useState, useContext, useEffect } from "react";
import {
  useCognito,
  signInWithEmail,
  signUpWithEmail,
  confirmUserSignUp,
  sendVerificationEmail,
  resetUserPassword,
  confirmUserPasswordReset,
  signOut,
  updateProfile,
  getCurrentUser,
} from "../../services/authUtils";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { BaseContext } from "../../contexts/BaseContext";
import {
  fetchUserSecrets,
  fetchVDBProfiles,
  fetchLlmProfiles,
} from "../../services/utils";
import { tokenService } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [errorJSX, setErrorJSX] = useState(null);
  const [success, setSuccess] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFormFadingOut, setIsFormFadingOut] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [greeting, setGreeting] = useState("Welcome");
  const [isVisible, setIsVisible] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [showResetCode, setShowResetCode] = useState(false);
  const [newPassword, setNewPassword] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  const { setSavedTags, setDeasyUserConfig, setVdbProfiles, setLlmProfiles } =
    useContext(BaseContext);
  const [searchParams] = useSearchParams();
  const marketplaceToken = searchParams.get("x-gcp-marketplace-token");
  const { setUser } = useAuth();

  // Generate time-based greeting only
  useEffect(() => {
    const hour = new Date().getHours();
    let timeGreeting = "Welcome";

    if (hour >= 5 && hour < 12) {
      timeGreeting = "Good morning";
    } else if (hour >= 12 && hour < 18) {
      timeGreeting = "Good afternoon";
    } else if (hour >= 18 && hour < 22) {
      timeGreeting = "Good evening";
    } else {
      timeGreeting = "Good evening";
    }

    setGreeting(timeGreeting);

    // Show card with slight delay for animation effect
    setTimeout(() => {
      setIsVisible(true);
    }, 100);
  }, []);

  const handleConfirmSignUp = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      await confirmUserSignUp(email, confirmationCode);
      setSuccess("Email verified successfully! You can now log in.");
      setShowConfirmation(false);
    } catch (error) {
      console.error("Error confirming sign up:", error);
      setError(error.message || "Failed to confirm registration.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      if (!showResetCode) {
        await resetUserPassword(email);
        setSuccess("Password reset code sent to your email!");
        setShowResetCode(true);
      } else {
        await confirmUserPasswordReset(email, resetCode, newPassword);
        setSuccess("Password has been reset successfully!");
        setShowResetCode(false);
        setIsResetPassword(false);
      }
    } catch (error) {
      console.error("Error in password reset:", error);
      setError(error.message || "Failed to process password reset.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setErrorJSX(null);
    setSuccess("");
    setIsLoading(true);

    // Special handling for password reset
    if (isResetPassword) {
      await handleResetPassword(e);
      return;
    }

    // Enforce that the user provides all required data during sign-up
    if (isSignUp) {
      if (
        !firstName.trim() ||
        !lastName.trim() ||
        !company.trim() ||
        !email.trim() ||
        !password.trim()
      ) {
        setError("Please fill out all fields before signing up.");
        setErrorJSX(null);
        setIsLoading(false);
        return;
      }
    }

    try {
      if (useCognito) {
        try {
          // Sign out existing user before attempting to sign in
          await signOut();
        } catch (signOutError) {}

        // AWS COGNITO AUTHENTICATION
        if (isSignUp) {
          await signUpWithEmail({
            username: email,
            password,
            options: {
              userAttributes: {
                email,
                given_name: firstName,
                family_name: lastName,
                "custom:company": company,
              },
            },
          });

          setSuccess(
            "Please check your email for a verification code and enter it below.",
          );
          setShowConfirmation(true);
          return;
        } else {
          // Cognito Login
          try {
            const { isSignedIn, nextStep } = await signInWithEmail({
              username: email,
              password,
            });

            // Handle Cognito challenges if needed
            if (
              nextStep &&
              nextStep.signInStep ===
                "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
            ) {
              setError(
                "You need to change your password. Please contact support.",
              );
              await signOut();
              setIsLoading(false);
              return;
            }

            if (!isSignedIn) {
              setError("Failed to sign in. Please try again.");
              setIsLoading(false);
              return;
            }

            const deasyUserConfig = await fetchUserSecrets(
              setSavedTags,
              setDeasyUserConfig,
            );
            if (deasyUserConfig.deasyApiKey) {
              await Promise.all([
                fetchVDBProfiles(setVdbProfiles, deasyUserConfig.deasyApiKey),
                fetchLlmProfiles(setLlmProfiles, deasyUserConfig.deasyApiKey),
              ]);
            }

            // Set the user in AuthContext before navigating
            const cognitoUser = await getCurrentUser();
            if (cognitoUser) {
              setUser({
                ...cognitoUser,
                email: cognitoUser?.attributes?.email,
                emailVerified: true,
                displayName:
                  `${cognitoUser?.attributes?.given_name || ""} ${cognitoUser?.attributes?.family_name || ""}`.trim(),
                authProvider: "cognito",
              });
            }

            setTimeout(() => {
              navigate(location.state?.from || "/");
            }, 300);
          } catch (error) {
            if (error.code === "UserNotConfirmedException") {
              setError("Email not verified. Please complete verification.");
              setShowConfirmation(true);
              setIsLoading(false);
              return;
            }
            throw error;
          }
        }
      } else {
        console.log("Using Firebase authentication");
        // FIREBASE AUTHENTICATION
        if (isSignUp) {
          const userCredential = await signUpWithEmail({
            email,
            password,
          });

          try {
            await updateProfile(userCredential.user, {
              displayName: `${firstName} ${lastName}`,
            });

            // Create or update subscription with profile data
            if (marketplaceToken) {
              await tokenService.linkMarketplaceAccount({
                marketplace_token: marketplaceToken,
                first_name: firstName || undefined,
                last_name: lastName || undefined,
                company: company || undefined,
              });
            } else {
              // Regular signup without marketplace token
              await tokenService.linkMarketplaceAccount({
                first_name: firstName || undefined,
                last_name: lastName || undefined,
                company: company || undefined,
              });
            }

            await sendVerificationEmail(userCredential.user);
            await signOut();
            setSuccess(
              "Please check your email to verify your account. Once email is verified, complete sign up by logging in.",
            );
            setIsSignUp(false);
          } catch (verificationError) {
            setError(
              "Account created but failed to send verification email. Please try again later.",
            );
          }
          return;
        } else {
          const userCredential = await signInWithEmail({
            email,
            password,
          });
          if (!userCredential.user.emailVerified) {
            setError("Please verify your email before logging in.");
            setErrorJSX(null);
            try {
              await sendVerificationEmail(userCredential.user);
              setSuccess(
                "A new verification email has been sent. Please check your inbox.",
              );
            } catch (verificationError) {
              setSuccess("Please check your inbox for the verification email.");
            }

            await signOut();
            setIsLoading(false);
            return;
          }

          // Fetch user secrets and check for deasy_verified
          const deasyUserConfig = await fetchUserSecrets(
            setSavedTags,
            setDeasyUserConfig,
          );
          if (deasyUserConfig.deasyApiKey) {
            await Promise.all([
              fetchVDBProfiles(setVdbProfiles, deasyUserConfig.deasyApiKey),
              fetchLlmProfiles(setLlmProfiles, deasyUserConfig.deasyApiKey),
            ]);
          }

          // Set the user in AuthContext before navigating
          const cognitoUser = await getCurrentUser();
          if (cognitoUser) {
            setUser({
              ...cognitoUser,
              email: cognitoUser?.attributes?.email,
              emailVerified: true,
              displayName:
                `${cognitoUser?.attributes?.given_name || ""} ${cognitoUser?.attributes?.family_name || ""}`.trim(),
              authProvider: "cognito",
            });
          }

          // Navigate after loading critical data
          setTimeout(() => {
            navigate(location.state?.from || "/");
          }, 300);
        }
      }
    } catch (err) {
      console.error("Authentication error:", err);
      console.log("Auth provider:", useCognito ? "Cognito" : "Firebase");
      console.log("Error details:", {
        code: err.code,
        name: err.name,
        message: err.message,
      });

      if (!useCognito) {
        // Firebase Error Handling
        if (isSignUp) {
          // Signup error handling
          if (err.code === "auth/email-already-in-use") {
            setError("An account with this email already exists.");
          } else if (err.code === "auth/invalid-email") {
            setError("Please enter a valid email address.");
          } else if (err.code === "auth/operation-not-allowed") {
            setError(
              "Email/password accounts are not enabled. Please contact support.",
            );
          } else if (err.code === "auth/weak-password") {
            setError("Password should be at least 6 characters long.");
          } else {
            setError("Failed to create account. Please try again.");
          }
        } else {
          // Login error handling
          switch (err.code) {
            case "auth/invalid-login-credentials":
            case "auth/invalid-credential":
            case "auth/user-not-found":
            case "auth/wrong-password":
              setError("Invalid email or password. Please try again.");
              break;
            case "auth/invalid-email":
              setError("Please enter a valid email address.");
              break;
            case "auth/user-disabled":
              setError(
                "This account has been disabled. Please contact support.",
              );
              break;
            case "auth/too-many-requests":
              setError("Too many failed attempts. Please try again later.");
              break;
            default:
              setError("An error occurred. Please try again.");
          }
        }
      } else {
        // Cognito Error Handling
        if (isSignUp) {
          if (err.name === "UsernameExistsException") {
            setError("An account with this email already exists.");
          } else if (
            err.name === "InvalidParameterException" &&
            err.message?.includes("password")
          ) {
            setError(
              "Password must be at least 8 characters and include uppercase, lowercase, numbers and special characters.",
            );
          } else if (err.name === "InvalidParameterException") {
            setError("Invalid email format. Please check your email address.");
          } else {
            setError(
              err.message || "Failed to create account. Please try again.",
            );
          }
        } else {
          // Login error handling
          if (err.name === "UserNotFoundException") {
            setError("No account found with this email.");
          } else if (err.name === "NotAuthorizedException") {
            setError("Incorrect username or password.");
          } else if (err.name === "UserNotConfirmedException") {
            setError("Please verify your email before logging in.");
            setShowConfirmation(true);
          } else {
            setError(err.message || "An error occurred. Please try again.");
          }
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleSignUp = () => {
    setIsFormFadingOut(true);
    setTimeout(() => {
      setIsSignUp(!isSignUp);
      setShowConfirmation(false);
      setShowResetCode(false);
      setTimeout(() => {
        setIsFormFadingOut(false);
      }, 10);
    }, 300);
  };

  // Render the appropriate form based on state
  const renderForm = () => {
    if (showConfirmation) {
      return (
        <form onSubmit={handleConfirmSignUp} className="space-y-6 mt-6">
          <div>
            <label
              htmlFor="confirmationCode"
              className="block text-gray-700 text-sm mb-2 text-left"
            >
              Verification Code
            </label>
            <input
              id="confirmationCode"
              type="text"
              placeholder="Enter verification code from email"
              className="block w-full px-4 py-3 text-gray-900 rounded-md border border-gray-200
                       focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400
                       placeholder-gray-400 text-sm bg-gray-50"
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value)}
              required
            />
          </div>

          <button
            type="submit"
            className="w-full bg-primary text-white py-3.5 rounded-md font-medium
                     hover:bg-primary-dark focus:outline-none transition-colors
                     disabled:opacity-70 disabled:cursor-not-allowed mt-4"
            disabled={isLoading}
          >
            {isLoading ? "Verifying..." : "Verify Email"}
          </button>

          <button
            type="button"
            onClick={() => setShowConfirmation(false)}
            className="w-full text-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            Back to Login
          </button>
        </form>
      );
    }

    if (isResetPassword) {
      if (showResetCode && useCognito) {
        return (
          <form onSubmit={handleResetPassword} className="space-y-6 mt-6">
            <div>
              <label
                htmlFor="resetCode"
                className="block text-gray-700 text-sm mb-2 text-left"
              >
                Reset Code
              </label>
              <input
                id="resetCode"
                type="text"
                placeholder="Enter code from email"
                className="block w-full px-4 py-3 text-gray-900 rounded-md border border-gray-200
                         focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400
                         placeholder-gray-400 text-sm bg-gray-50"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                required
              />
            </div>

            <div>
              <label
                htmlFor="newPassword"
                className="block text-gray-700 text-sm mb-2 text-left"
              >
                New Password
              </label>
              <input
                id="newPassword"
                type="password"
                placeholder="Enter new password"
                className="block w-full px-4 py-3 text-gray-900 rounded-md border border-gray-200
                         focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400
                         placeholder-gray-400 text-sm bg-gray-50"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              {useCognito && (
                <p className="text-xs text-gray-500 mt-1">
                  Password must be at least 8 characters with uppercase,
                  lowercase, numbers and special characters.
                </p>
              )}
            </div>

            <button
              type="submit"
              className="w-full bg-primary text-white py-3.5 rounded-md font-medium
                       hover:bg-primary-dark focus:outline-none transition-colors
                       disabled:opacity-70 disabled:cursor-not-allowed mt-4"
              disabled={isLoading}
            >
              {isLoading ? "Resetting..." : "Reset Password"}
            </button>

            <button
              type="button"
              onClick={() => {
                setIsResetPassword(false);
                setShowResetCode(false);
              }}
              className="w-full text-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Back to Login
            </button>
          </form>
        );
      }

      return (
        <form onSubmit={handleResetPassword} className="space-y-6 mt-6">
          <div>
            <label
              htmlFor="email"
              className="block text-gray-700 text-sm mb-2 text-left"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              placeholder="you@example.com"
              className="block w-full px-4 py-3 text-gray-900 rounded-md border border-gray-200
                       focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400
                       placeholder-gray-400 text-sm bg-gray-50"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <button
            type="submit"
            className="w-full bg-primary text-white py-3.5 rounded-md font-medium
                     hover:bg-primary-dark focus:outline-none transition-colors
                     disabled:opacity-70 disabled:cursor-not-allowed mt-4"
            disabled={isLoading}
          >
            {isLoading ? "Sending..." : "Send Reset Instructions"}
          </button>

          <button
            type="button"
            onClick={() => setIsResetPassword(false)}
            className="w-full text-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            Back to Login
          </button>
        </form>
      );
    }

    // Default login/signup form
    return (
      <form onSubmit={handleSubmit} className="space-y-6 mt-6">
        {/* Additional fields for Sign Up */}
        {isSignUp && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-gray-700 text-sm font-medium mb-1 text-left"
                >
                  First Name
                </label>
                <input
                  id="firstName"
                  type="text"
                  placeholder="First Name"
                  className="block w-full px-3 py-2 text-gray-900 rounded-md border border-gray-300
                             focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400
                             placeholder-gray-400 text-sm"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              <div>
                <label
                  htmlFor="lastName"
                  className="block text-gray-700 text-sm font-medium mb-1 text-left"
                >
                  Last Name
                </label>
                <input
                  id="lastName"
                  type="text"
                  placeholder="Last Name"
                  className="block w-full px-3 py-2 text-gray-900 rounded-md border border-gray-300
                             focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400
                             placeholder-gray-400 text-sm"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="company"
                className="block text-gray-700 text-sm font-medium mb-1 text-left"
              >
                Company
              </label>
              <input
                id="company"
                type="text"
                placeholder="Company Name"
                className="block w-full px-3 py-2 text-gray-900 rounded-md border border-gray-300
                           focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400
                           placeholder-gray-400 text-sm"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                required
              />
            </div>
          </>
        )}

        {/* Email Field */}
        <div>
          <label
            htmlFor="email"
            className="block text-gray-700 text-sm mb-2 text-left"
          >
            Email
          </label>
          <input
            id="email"
            type="email"
            placeholder="you@example.com"
            className="block w-full px-4 py-3 text-gray-900 rounded-md border border-gray-200
                       focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400
                       placeholder-gray-400 text-sm bg-gray-50"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        {/* Password Field */}
        <div>
          <label
            htmlFor="password"
            className="block text-gray-700 text-sm mb-2 text-left"
          >
            Password
          </label>
          <input
            id="password"
            type="password"
            placeholder="••••••••"
            className="block w-full px-4 py-3 text-gray-900 rounded-md border border-gray-200
                         focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400
                         placeholder-gray-400 text-sm bg-gray-50"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {useCognito && isSignUp && (
            <p className="text-xs text-gray-500 mt-1">
              Password must be at least 8 characters with uppercase, lowercase,
              numbers and special characters.
            </p>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="w-full bg-primary text-white py-3.5 rounded-md font-medium
                     hover:bg-primary-dark focus:outline-none transition-colors
                     disabled:opacity-70 disabled:cursor-not-allowed mt-4"
          disabled={isLoading}
        >
          {isLoading
            ? isSignUp
              ? "Setting up..."
              : "Signing in..."
            : isSignUp
              ? "Sign Up"
              : "Login"}
        </button>
      </form>
    );
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <div
        className={`
        w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-8 shadow-md
        transition-all duration-500 ease-in-out border border-gray-100
        ${isFormFadingOut ? "opacity-0 scale-95" : "opacity-100 scale-100"}
        ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}
      `}
      >
        {/* Logo and Title - Clean presentation */}
        <div className="mb-10 flex flex-col items-center">
          <div className="mb-7 flex items-center justify-center">
            <div
              className="relative flex items-center justify-center h-24 w-24 overflow-hidden bg-white rounded-full shadow-sm cursor-pointer transition-all duration-300"
              onClick={() =>
                window.open(
                  "https://www.deasylabs.com",
                  "_blank",
                  "noopener,noreferrer",
                )
              }
            >
              <img
                src="/deasy-icon.png"
                alt="DeasyLabs"
                className="h-20 w-20 relative z-10 rounded-full"
              />
            </div>
          </div>

          {!isSignUp && !isResetPassword && !showConfirmation && (
            <>
              <h2 className="text-2xl font-medium text-gray-800 mb-1">
                Welcome to Deasy
              </h2>
              <p className="text-sm text-gray-500">{greeting}</p>
            </>
          )}

          {isSignUp && !showConfirmation && (
            <h2 className="text-2xl font-medium text-gray-800">Sign Up</h2>
          )}

          {isResetPassword && !showConfirmation && (
            <h2 className="text-2xl font-medium text-gray-800">
              Reset Password
            </h2>
          )}

          {showConfirmation && (
            <h2 className="text-2xl font-medium text-gray-800">
              Verify Your Email
            </h2>
          )}
        </div>

        {/* Error / Success Messages */}
        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded mb-4 text-sm border border-red-200 text-left">
            {error}
          </div>
        )}
        {errorJSX && (
          <div className="bg-red-50 text-red-700 p-3 rounded mb-4 text-sm border border-red-200 text-left">
            {errorJSX}
          </div>
        )}
        {success && (
          <div className="bg-green-50 text-green-700 p-3 rounded mb-4 text-sm border border-green-200 text-left">
            {success}
          </div>
        )}

        {/* Render the appropriate form based on state */}
        {renderForm()}

        {/* Loading Spinner (conditionally rendered) */}
        {isLoading && (
          <div className="flex items-center justify-center mt-2">
            <div className="w-5 h-5 border-2 border-t-transparent border-emerald-500 rounded-full animate-spin"></div>
          </div>
        )}

        {/* Account Links - Only show in main login/signup form */}
        {!showConfirmation && !isResetPassword && (
          <div className="mt-8 flex flex-col space-y-4">
            <button
              onClick={handleToggleSignUp}
              className="w-full text-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              {isSignUp
                ? "Already have an account? Login"
                : "Need an account? Sign Up"}
            </button>
            <button
              onClick={() => setIsResetPassword(true)}
              className="w-full text-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Forgot Password?
            </button>
          </div>
        )}

        {/* Footer - Simple tagline with border */}
        <div className="mt-12 text-center border-t border-gray-100 pt-6">
          <p className="text-xs text-gray-400">
            Simplify complexity, amplify intelligence
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
